import { useContext, useMemo, useState } from "react";
import { Segmented, Space } from "antd";
import dayjs from "dayjs";
import AuthContext from "../../../context/AuthContext";
import {
  useReportActivity, useReportByAssignee, useReportGantt, useReportOverview,
  useReportTasksByProject, useReportTicketResponse,
} from "../../../queries/reports";
import { useProjects } from "../../../queries/projects";
import { useUsers } from "../../../queries/users";
import { PageShell } from "../../ui/PageShell";
import { AssigneeSection } from "./AssigneeSection";
import { KpiBand } from "./KpiBand";
import { ProjectsTable } from "./ProjectsTable";
import { ResponseSection } from "./ResponseSection";
import { TasksByProjectSection } from "./TasksByProjectSection";
import { TimelineSection } from "./TimelineSection";
import { rangeFor } from "./format";
import { ExportButtons } from "../reports/ExportButtons";
import styles from "./dash.module.css";

type Days = 7 | 30 | 90;

export const ManagerDashboard = () => {
  const { userData } = useContext(AuthContext);
  const [days, setDays] = useState<Days>(30);
  const { from, to } = useMemo(() => rangeFor(days), [days]);
  const rangeLabel = `last ${days} days`;
  const granularity = days === 7 ? "day" : "week";

  const overview = useReportOverview();
  const byAssignee = useReportByAssignee(from, to);
  const tasksByProject = useReportTasksByProject();
  const response = useReportTicketResponse(from, to);
  const gantt = useReportGantt();
  const activity = useReportActivity(from, to, granularity);
  const projects = useProjects();
  const users = useUsers();

  const hour = dayjs().hour();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const state = (q: { isLoading: boolean; isError: boolean; refetch: () => unknown }) => ({
    loading: q.isLoading, error: q.isError, onRetry: () => { q.refetch(); },
  });

  return (
    <PageShell
      kind="dashboard"
      kicker={dayjs().format("dddd, D MMMM")}
      title={`${greeting}, ${(userData?.name || "").split(" ")[0]}`}
      subtitle="How support tickets and project work are being handled."
      actions={
        <Space wrap>
        <ExportButtons from={from} to={to} />
        <Segmented
          aria-label="Date range"
          value={days}
          onChange={(v) => setDays(v as Days)}
          options={[
            { label: "7 days", value: 7 },
            { label: "30 days", value: 30 },
            { label: "90 days", value: 90 },
          ]}
          className={styles.segmented}
        />
        </Space>
      }
    >
      <div className={styles.stack}>
        <KpiBand overview={overview.data} response={response.data} rangeLabel={rangeLabel} />
        <ProjectsTable
          rows={tasksByProject.data} projects={projects.data} users={users.data}
          {...state(tasksByProject)}
        />
        <AssigneeSection rows={byAssignee.data} rangeLabel={rangeLabel} {...state(byAssignee)} />
        <TasksByProjectSection rows={tasksByProject.data} {...state(tasksByProject)} />
        <ResponseSection report={response.data} {...state(response)} />
        <TimelineSection
          activity={activity.data} granularity={granularity} activityState={state(activity)}
          gantt={gantt.data} ganttState={state(gantt)}
        />
      </div>
    </PageShell>
  );
};
